/* eslint-disable sonarjs/cognitive-complexity */
'use client';

import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from '@ahafood/ui/components/avatar';
import { Button } from '@ahafood/ui/components/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@ahafood/ui/components/card';
import { assetPrefix } from '@ahm/api-wrappers-core';
import { cn } from '@ahm/common-helpers';
import { Star } from 'lucide-react';
import { ItemDescription } from './item-description';
import type { ItemList, User } from './types';
import {
  useChatbotInteracted,
  chatbotInteractedFn,
} from '@/api/use-chatbot-interacted';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { useRouter } from '@/i18n/routing';
import { getAuthToken } from '@/lib/auth/get-token';
import { trackEvent } from '@/lib/event-tracking';

interface ChatItemListProps {
  items: ItemList[];
  showAllItems: boolean;
  onToggleShowAll: () => void;
  onItemClick: (item: ItemList) => void;
  user?: User;
  threadId: string;
  itemDescriptions?: Record<string, string>;
  isStreamingDescriptions?: boolean;
}

export function ChatItemList({
  items,
  showAllItems,
  onToggleShowAll,
  onItemClick,
  user,
  threadId,
  itemDescriptions = {},
  isStreamingDescriptions = false,
}: ChatItemListProps) {
  const token = getAuthToken();
  const routes = useCityRoutes();
  const router = useRouter();
  const { mutate: chatbotInteracted } = useChatbotInteracted({
    mutationFn: (params) =>
      chatbotInteractedFn(params, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      }),
  });

  const handleCardClick = ({ item }: { item: ItemList }) => {
    if (item.store_id) {
      chatbotInteracted({ store_id: Number(item.store_id) });
    }
    trackEvent('chatbot_event', {
      action_time: Math.floor(Date.now() / 1000),
      button_name: 'chatbot_item_click',
      timestamp: Date.now(),
      user_id: user?.cunstomerId,
      thread_id: threadId,
    });
    router.push(routes.storeDetail(item.slug));
    if (
      typeof window !== 'undefined' &&
      typeof window.setOpenChange === 'function'
    ) {
      window.setOpenChange(false);
    }
  };

  const displayItems = showAllItems ? items : items.slice(0, 5);

  if (items.length === 0) {
    return null;
  }

  return (
    <div className="w-full space-y-2" data-chat-item-list="true">
      <div className="flex flex-col gap-3 overflow-y-auto pb-2">
        {displayItems.map((item) => {
          const isSoloMeal = item.extra_tags.includes('SOLO_MEAL');
          // Rating and total_sold display logic
          let rawRating: number | null = null;

          // Ưu tiên average
          if (item.rating?.average && item.rating.average > 0) {
            rawRating = item.rating.average;
          } else if (
            item.rating?.google_average &&
            item.rating.google_average >= 4 &&
            item.rating.total === 0
          ) {
            rawRating = item.rating.google_average;
          }

          const showRating = rawRating !== null; // chỉ show khi có giá trị hợp lệ
          const showSold = item.total_sold > 10;
          let soldDisplay = '';
          if (showSold) {
            soldDisplay =
              item.total_sold > 999
                ? `${(item.total_sold / 1000).toFixed(1)}K`
                : `${item.total_sold}`;
          }
          const ratingElement = showRating ? (
            <div className="flex items-center">
              {item.rating?.total === 0 ? (
                <img
                  src="/static/images/gg-rating.webp"
                  alt="Store"
                  className="inline-block mr-1 h-auto w-4 mb-0.5"
                />
              ) : (
                <Star className="size-3 mr-1 fill-yellow-40 text-yellow-40" />
              )}
              <span className="font-bold">{rawRating!.toFixed(1)}</span>
            </div>
          ) : null;
          const soldElement = showSold ? (
            <span>
              <img
                src="/static/icons/bag.svg"
                alt="Store"
                className="inline-block size-3 mr-1"
              />
              {soldDisplay} lượt bán
            </span>
          ) : null;

          return (
            <Card
              onClick={() => handleCardClick({ item })}
              key={item._id}
              className={cn(
                'rounded-lg shadow-none',
                isSoloMeal
                  ? 'bg-gradient-to-r from-[#FFE58F] via-[#FFEED9] to-[#FCB3AD] p-px border-none'
                  : 'bg-white p-0'
              )}
            >
              <div className="shrink-0 w-full bg-white overflow-hidden flex flex-col p-3 rounded-lg">
                <div className="flex flex-row gap-2">
                  {/* Left section: Image and "Solo Meal" badge */}
                  <div className="relative min-w-[120px] size-1/3 ">
                    <Avatar className="size-full rounded-lg">
                      <AvatarImage
                        className="object-cover size-full aspect-square"
                        width={120}
                        height={120}
                        src={
                          item.image_thumb_url ||
                          '/static/images/empty-store-bot.webp'
                        }
                      />
                      <AvatarFallback>{item.name}</AvatarFallback>
                    </Avatar>
                    <div
                      style={{
                        backgroundImage: `url(${assetPrefix}/static/images/eta-group.webp)`,
                        backgroundSize: 'contain',
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'left bottom',
                      }}
                      className="absolute bottom-0 left-0 flex items-center w-24 h-6"
                    >
                      <span className="text-[10px] text-center w-full font-semibold pt-1 pr-1">
                        Từ {item.eta} phút
                      </span>
                    </div>
                    {/* <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        onItemClick(item);
                      }}
                      className="absolute bottom-2 right-2 flex items-center size-5 rounded-full bg-primary-10 p-1 shadow-md [&_svg]:size-3"
                    >
                      <PlusIcon className="text-primary-40" />
                    </Button> */}
                  </div>

                  {/* Right section: Details and Actions */}
                  <div className="flex-1 flex flex-col justify-between">
                    <CardHeader className="p-0">
                      <div className="flex items-center space-x-2">
                        {isSoloMeal && (
                          <img
                            src="/static/images/solo-meal-tag.webp"
                            alt="Solo Meal"
                            className="inline-block w-20 h-auto mr-1"
                          />
                        )}
                      </div>
                      <CardTitle className="text-sm font-bold text-neutral-80 line-clamp-1">
                        {item.name}
                      </CardTitle>
                    </CardHeader>

                    <div
                      className={cn(
                        'text-neutral-70 rounded-lg px-2 py-1.5 text-xs gap-0.5 flex flex-col items-start',
                        isSoloMeal
                          ? 'bg-gradient-to-r from-[#FFEAA8]/70 via-[#FFEFDC]/70 to-[#FDC9B9]/70'
                          : 'bg-neutral-10/80'
                      )}
                    >
                      <div
                        className={cn(
                          'flex  text-neutral-50 text-xs h-auto',
                          (ratingElement ?? soldElement)
                            ? 'items-center'
                            : 'items-start'
                        )}
                      >
                        <img
                          src="/static/icons/store.svg"
                          alt="Store"
                          className="inline-block size-3 mr-1"
                        />
                        <span
                          className={cn(
                            'font-bold',
                            (ratingElement ?? soldElement)
                              ? 'line-clamp-1'
                              : 'line-clamp-2'
                          )}
                        >
                          {item.distance.toFixed(1)} km -{' '}
                          <span className="font-medium">{item.page_name}</span>
                        </span>
                      </div>
                      {(ratingElement ?? soldElement) && (
                        <div className="flex items-start text-neutral-50 text-xs h-3">
                          {ratingElement}
                          {ratingElement && soldElement && (
                            <span className="mx-2 text-neutral-50">|</span>
                          )}
                          {soldElement}
                        </div>
                      )}
                    </div>

                    <CardContent className="p-0 flex items-center justify-between mt-1">
                      <div className="flex items-baseline space-x-2">
                        {item.discount_price ? (
                          <div className="flex gap-1 items-end flex-wrap">
                            <span className="font-bold text-primary-40">
                              {item.discount_price.toLocaleString()} đ
                            </span>
                            <span className="line-through text-neutral-40 mr-2 text-sm">
                              {item.price.toLocaleString()} đ
                            </span>
                          </div>
                        ) : (
                          <span className="font-bold">
                            {item.price.toLocaleString()} đ
                          </span>
                        )}
                      </div>
                      <Button
                        onClick={(e) => {
                          e.stopPropagation();
                          onItemClick(item);
                        }}
                        className="bg-gradient-to-r from-[#FFA339] to-[#FF7F32] text-white font-bold py-1.5 px-3 rounded-lg flex flex-col h-auto gap-0"
                      >
                        Đặt ngay
                        {isSoloMeal && (
                          <span className="font-normal text-xs">
                            Hợp 1 người
                          </span>
                        )}
                      </Button>
                    </CardContent>
                  </div>
                </div>

                {/* Item Description - only show for first 5 items */}
                {displayItems.indexOf(item) < 5 && (
                  <ItemDescription
                    itemId={item._id}
                    description={itemDescriptions[item._id] || ''}
                    isLoading={
                      isStreamingDescriptions && !itemDescriptions[item._id]
                    }
                    isSoloMeal={isSoloMeal}
                  />
                )}

                {/* <CardFooter className="p-0 text-neutral-60 text-sm leading-relaxed border-t border-dashed mt-2 pt-1">
                  <img
                    src="/static/images/item-note.webp"
                    alt="Solo Meal"
                    className="inline-block w-6 h-auto"
                  />
                  <span className="ml-1.5 text-xs">
                    {' '}
                    Dành cho 1 người ăn (bún + đậu + thịt luộc + chả cốm + dồi
                    sụn + nem chua rán). Dành cho 1 người...{' '}
                    <a href="#" className="text-primary hover:underline">
                      Xem thêm
                    </a>
                  </span>
                </CardFooter> */}
              </div>
            </Card>
          );
        })}

        {items.length > 4 && (
          <div className="flex justify-center">
            <Button variant="ghost" onClick={onToggleShowAll}>
              {showAllItems ? 'Ẩn bớt' : 'Xem thêm'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
